import { ActionTree } from 'vuex';
import { dexMutation } from '@/helpers/graphql';
import { CommentState } from './state';
import RootState from '@/store/root_state';
import {
  createUserThinkAndAnswerComment,
  createNestedCommentTextAndContent,
  createUserDiscursiveExerciseComment,
  updateCommentById,
  updateUserPracticeActivityComment,
} from '../../queries/dexQueries';
import { ErrorHandler } from '@/helpers/errorHandler';
import i18n from '../../../i18n';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import { Exercise, UndergradLesson } from '@/models/undergrad/undegrad-lesson.model';
import { UndergradDiscipline } from '@/models/undergrad/undergrad-discipline.model';
import { UserModel } from '@descomplica/dex-models';
import {
  ExercisesEvaluationService,
  DiscursiveExerciseType,
} from '@/services/undergrad/pegasus-pepe-legal/exercises-evaluation.service';

const actions: ActionTree<CommentState, RootState> = {
  async CREATE_THINK_AND_ANSWER_COMMENT(
    { getters },
    payload: {
      exercise: Exercise;
      discipline: UndergradDiscipline;
      lesson: UndergradLesson;
      user: UserModel;
      text: string;
      studyWindowId: string;
    },
  ): Promise<{ success: boolean }> {
    try {
      const { exercise, user, text, discipline, lesson, studyWindowId } = payload;
      let createdComment;

      if (getters.gradeActivitiesViaAi) {
        const course = getters.currentUndergradCourse;

        createdComment = await new ExercisesEvaluationService().sendDiscursiveExerciseAnswer(
          DiscursiveExerciseType.DISCURSIVE_EXERCISE,
          {
            body: text,
            dexExerciseId: exercise.dexExerciseId,
            exerciseId: exercise.id,
            courseId: course.id,
            disciplineInstanceId: discipline.disciplineInstanceId,
            lessonId: lesson.id,
            studyWindowId,
          },
        );
      } else {
        const response = await dexMutation(createUserThinkAndAnswerComment, {
          questionId: exercise.dexExerciseId,
          text,
        });

        createdComment = response.data.createUserThinkAndAnswerComment;
      }

      if (!createdComment.success) {
        ErrorHandler.showErrorMessage({
          error: new Error(i18n.t('itWasNotPossibleToCreateStudentComment').toString()),
        });

        return { success: false };
      }

      exercise.comments = [
        new UnifiedCommentModel(
          {
            id: createdComment.comment.id,
            updatedAt: createdComment.comment.updatedAt,
            score: null,
            attemptCount: null,
            textId: createdComment.text.id,
            content: text,
            exerciseCommentAttachments: createdComment.exerciseCommentAttachments || [],
          },
          user,
        ),
      ];

      return { success: true };
    } catch (e) {
      ErrorHandler.showErrorMessage({
        error: new Error(i18n.t('defaultErrorMessage').toString()),
      });

      return { success: false };
    }
  },

  async CREATE_COMMENT({ commit }, payload) {
    try {
      const { lesson, question, user, body, attemptCount = null } = payload;

      const {
        data: {
          createNestedCommentTextAndContent: { nestedCommentTextAndContent: createdComment },
        },
      } = await dexMutation(createNestedCommentTextAndContent, {
        questionId: question.dexExerciseId,
        userId: user.id,
        text: body,
        attemptCount,
      });

      const comment = new UnifiedCommentModel(
        {
          id: createdComment.comment.id,
          updatedAt: createdComment.comment.updatedAt,
          score: null,
          attemptCount,
          textId: createdComment.comment.contentsByCommentId.nodes[0].textByTextId.id,
          content: body,
          exerciseCommentAttachments: [],
        },
        user,
      );

      commit('ADD_LESSON_COMMENT', { lessonId: lesson.id, itemId: question.id, comment });
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToCreateUserComment').toString(),
      });
    }
  },

  async CREATE_EXERCISE_COMMENT(
    { getters },
    payload: {
      exercise: Exercise;
      user: UserModel;
      discipline: UndergradDiscipline;
      lesson: UndergradLesson;
      body: string;
      attemptCount: number;
      attachments: [];
      studyWindowId: string;
    },
  ): Promise<{ success: boolean }> {
    try {
      const {
        exercise,
        user,
        body,
        discipline,
        lesson,
        attemptCount,
        attachments = [],
        studyWindowId,
      } = payload;

      let createdComment;

      if (getters.gradeActivitiesViaAi) {
        const course = getters.currentUndergradCourse;

        createdComment = await new ExercisesEvaluationService().sendDiscursiveExerciseAnswer(
          DiscursiveExerciseType.PRACTICE_EXERCISE,
          {
            body,
            dexExerciseId: exercise.dexExerciseId,
            exerciseId: exercise.id,
            courseId: course.id,
            disciplineInstanceId: discipline.disciplineInstanceId,
            lessonId: lesson.id,
            attemptCount,
            attachments,
            studyWindowId,
          },
        );
      } else {
        const {
          data: { createUserExerciseComment: result },
        } = await dexMutation(createUserDiscursiveExerciseComment, {
          questionId: exercise.dexExerciseId,
          body,
          attemptCount,
          attachments,
        });

        createdComment = result;
      }

      exercise.comments = [
        new UnifiedCommentModel(
          {
            id: createdComment.comment.id,
            updatedAt: createdComment.comment.updatedAt,
            score: null,
            attemptCount,
            textId: createdComment.text.id,
            content: body,
            exerciseCommentAttachments: createdComment.exerciseCommentAttachments || [],
          },
          user,
        ),
      ];

      return { success: true };
    } catch (e) {
      ErrorHandler.showErrorMessage({
        error: new Error(i18n.t('defaultErrorMessage').toString()),
      });

      return { success: false };
    }
  },

  async UPDATE_EXERCISE_COMMENT(
    { getters },
    payload: {
      exercise: Exercise;
      comment: UnifiedCommentModel;
      body: string;
      attachments: [];
      studyWindowId: string;
    },
  ): Promise<{ success: boolean }> {
    try {
      const { exercise, comment, body, attachments = [], studyWindowId } = payload;

      let updatedComment;

      if (getters.gradeActivitiesViaAi) {
        updatedComment = await new ExercisesEvaluationService().updatePracticeExerciseAnswer({
          body,
          exerciseId: exercise.id,
          dexExerciseId: exercise.dexExerciseId,
          dexTextId: comment.textId!,
          dexCommentId: comment.id,
          attachments,
          studyWindowId,
        });
      } else {
        const response = await dexMutation(updateUserPracticeActivityComment, {
          textId: comment.textId,
          questionId: exercise.dexExerciseId,
          body,
          commentId: comment.id,
          attachments,
        });

        updatedComment = response.data.updateUserPracticeActivityComment;
      }

      if (!updatedComment.success) {
        ErrorHandler.showErrorMessage({
          error: new Error(i18n.t('itWasNotPossibleToUpdateStudentComment').toString()),
        });

        return { success: false };
      }

      comment.attemptCount = (comment.attemptCount || 0) + 1;
      comment.text = body;
      comment.timeSinceCreation = i18n.tc('now').toLowerCase();

      return { success: true };
    } catch (e) {
      ErrorHandler.showErrorMessage({
        error: new Error(i18n.t('defaultErrorMessage').toString()),
      });
      return { success: false };
    }
  },

  async UPDATE_COMMENT_SCORE({ commit }, comment: UnifiedCommentModel) {
    try {
      commit('CHANGE_COMMENT_SCORE_UPDATING_STATUS', {
        commentId: comment.id,
        updating: true,
      });

      await dexMutation(updateCommentById, {
        commentId: comment.id,
        score: comment.score,
      });

      commit('SET_UPDATED_COMMENT_SCORE', {
        commentId: comment.id,
        score: comment.score,
      });
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToUpdateStudentScore').toString(),
      });
    }

    commit('CHANGE_COMMENT_SCORE_UPDATING_STATUS', {
      commentId: comment.id,
      updating: false,
    });
  },
};

export default actions;
